import { v4 as uuid } from "uuid";
import {
  CalendarIcon,
  GridIcon,
  MCQMultiIcon,
  MCQSingleIcon,
  NumberIcon,
  ShortTextIcon,
  StatementIcon,
  UploadIcon,
  SliderIcon,
  DropdownIcon,
  CalculatedIcon,
  RepeatedDataIcon,
  RankOrderIcon,
} from "./Icons";

export const displayTypes = [
  "text",
  "date",
  "slider",
  "calculated_field",
  "radio",
  "checkbox",
  "dropdown",
  "number",
  "rank_order",
];

export const textTypes = ["text", "date"];
export const number_types = ["number", "slider", "calculated_field"];
export const choice_types = ["radio", "checkbox", "dropdown", "rank_order"];
export const no_validation_types = [
  "statement",
  "upload_file",
  "grid",
  "repeated_data",
  "rank_order",
];
export const no_advanced_types = ["statement"];

export const noValueTypes = ["statement", "rank_order"];

export const IconMap: any = {
  radio: MCQSingleIcon,
  checkbox: MCQMultiIcon,
  dropdown: DropdownIcon,
  text: ShortTextIcon,
  number: NumberIcon,
  date: CalendarIcon,
  statement: StatementIcon,
  slider: SliderIcon,
  upload_file: UploadIcon,
  calculated_field: CalculatedIcon,
  grid: GridIcon,
  repeated_data: RepeatedDataIcon,
  rank_order: RankOrderIcon,
};

export const QuestionLabelMap: any = {
  radio: "Multiple Choice",
  checkbox: "Checkbox",
  text: "Text Field",
  date: "Date Field",
  number: "Number Field",
  upload_file: "Upload file Field",
  grid: "Grid Field",
  statement: "Statement",
  dropdown: "Dropdown Field",
  slider: "Slider Field",
  calculated_field: "Calculated Field",
  repeated_data: "Repeated Data Field",
  rank_order: "Rank Order",
};

export const questionTypes = [
  {
    key: "1",
    type: "radio",
    title: "Multiple Choice",
    icon: IconMap["radio"],
  },
  {
    key: "2",
    title: "Checkbox",
    type: "checkbox",
    icon: IconMap["checkbox"],
  },
  {
    key: "3",
    title: "Text",
    type: "text",
    icon: IconMap["text"],
  },
  {
    key: "4",
    title: "Number",
    type: "number",
    icon: IconMap["number"],
  },
  {
    key: "5",
    title: "Date",
    type: "date",
    icon: IconMap["date"],
  },
  {
    key: "6",
    title: "Upload file",
    type: "upload_file",
    icon: IconMap["upload_file"],
  },
  {
    key: "7",
    title: "Grid",
    type: "grid",
    icon: IconMap["grid"],
  },
  {
    key: "8",
    title: "Statement",
    type: "statement",
    icon: IconMap["statement"],
  },
  {
    key: "9",
    title: "Slider",
    type: "slider",
    icon: IconMap["slider"],
  },
  {
    key: "10",
    title: "Dropdown",
    type: "dropdown",
    icon: IconMap["dropdown"],
  },
  {
    key: "11",
    title: "Calculated Field",
    type: "calculated_field",
    icon: IconMap["calculated_field"],
  },
  {
    key: "12",
    title: "Repeated Data",
    type: "repeated_data",
    icon: IconMap["repeated_data"],
  },
  {
    key: "13",
    title: "Rank Order",
    type: "rank_order",
    icon: IconMap["rank_order"],
  },
];

export const supportedTypes = questionTypes.map((question) => question.type);

const basicField = {
  label: "",
  varname: "",
  helperText: "",
  validations: [],
};

// TODO
const choiceField = () => {
  return {
    ref: uuid(),
    label: "",
    position: 1,
    value: "",
    isOther: false,
  };
};

const multipleChoiceField = () => ({
  ...basicField,
  excludeExport: false,
  properties: {
    required: false,
    orientation: "vertical",
    remarkText: "",
    bulletStyle: "alphabetical",
  },
  choices: [choiceField()],
});

const dropdownField = () => ({
  ...basicField,
  excludeExport: false,
  properties: {
    required: false,
    remarkText: "",
  },
  choices: [choiceField()],
});

const textField = {
  ...basicField,
  isEncrypted: false,
  excludeExport: false,
  properties: {
    required: false,
    allowMultiline: false,
    remarkText: "",
    format: "",
  },
};

const numberField = {
  ...basicField,
  isEncrypted: false,
  excludeExport: false,
  properties: {
    required: false,
    allowDecimal: false,
    measurementUnit: "",
    remarkText: "",
  },
};

const dateField = {
  ...basicField,
  isEncrypted: false,
  excludeExport: false,
  properties: {
    required: false,
    format: "DD/MM/YYYY",
  },
};

const sliderField = {
  ...basicField,
  isEncrypted: false,
  excludeExport: false,
  properties: {
    required: false,
    minLabel: "",
    maxLabel: "",
    remarkText: "",
    min: 0,
    max: 100,
    step: 1,
  },
};

const uploadField = {
  ...basicField,
  excludeExport: false,
  properties: {
    required: false,
    remarkText: "",
    max: 2,
  },
};

const calculatedField = {
  ...basicField,
  excludeExport: false,
  calculationTemplate: "",
  properties: {
    required: false,
    remarkText: "",
    isHidden: false,
    responseType: "number",
    executionType: "auto",
    defaults: [],
  },
};

const statementField = {
  ...basicField,
  excludeExport: true,
  properties: {},
};

const gridField = {
  ...basicField,
  excludeExport: false,
  properties: {
    required: false,
    remarkText: "",
    gridConfig: {
      fieldType: "row",
      rows: [{ label: "", type: "text" }],
      columns: [{ label: "" }],
    },
  },
};

const repeatedField = {
  ...basicField,
  excludeExport: false,
  properties: {
    required: false,
    remarkText: "",
    repeatedConfig: {
      columns: [
        {
          label: "",
          type: "text",
        },
      ],
    },
  },
};

const rankOrderField = () => ({
  ...basicField,
  excludeExport: false,
  properties: {
    required: false,
    remarkText: "",
  },
  choices: [choiceField()],
});

export const fieldMap = (type: string) => {
  switch (type) {
    case "radio":
      return multipleChoiceField();
    case "checkbox":
      return multipleChoiceField();
    case "dropdown":
      return dropdownField();
    case "text":
      return textField;
    case "date":
      return dateField;
    case "slider":
      return sliderField;
    case "number":
      return numberField;
    case "upload_file":
      return uploadField;
    case "statement":
      return statementField;
    case "grid":
      return gridField;
    case "repeated_data":
      return repeatedField;
    case "calculated_field":
      return calculatedField;
    case "rank_order":
      return rankOrderField();
    default:
      return {};
  }
};
